import React, { FormEvent, useEffect, useState } from "react";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import { formValue } from "@/src/services/formServiceFunctions";
import _get from "lodash/get";
import alimonyTranslations from "@/src/services/translations/alimonyTranslations";
import { Link, useLocation, useNavigate } from "react-router";
import Grid from "@mui/material/Grid";
import PersonData from "./Summary/PersonData";
import AlimonyData from "./Summary/AlimonyData";
import AlimonyChildren from "./Summary/AlimonyChildren";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";
import Button from "@mui/material/Button";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import { toCheckout } from "@/src/services/checkoutService";
import ContactData from "./Summary/ContactData";
import { getCaseLink } from "@/src/services/productService";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import summaryStyle from "@/src/components/Form/Common/Style/summaryStyle";
import { Box } from "@mui/material";

type SummaryProps = { product: AbstractCase; withInsurance?: boolean };

export default function Summary({ product, withInsurance = true }: SummaryProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { values, handleSubmit } = useForm();
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);

  const stateField = location.state?.field;

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  const basePath = getCaseLink(product) + "/antrag";

  const translate = (path: string) => {
    return _get(alimonyTranslations, path);
  };

  const handleCheckout = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    await updateMutation.mutateAsync({
      uri: "/alimonies",
      id: product.id,
      data: { personalTextMessage: formValue(values, "personalTextMessage") },
    });
    await toCheckout(product.checkoutItem.id + "_" + product.id, navigate);
  };

  return (
    <ProductBox>
      <Typography variant={"h5"}>
        Ihre Angaben
      </Typography>
      <Grid container>
        {values.basicChoice !== "other" && (
          <>
            <Box id={"alimonyPetitionerData"} sx={summaryStyle.scrollToAnchor} />

            <Box id={"alimony"} sx={summaryStyle.scrollToAnchor} />
            <AlimonyData formData={values} />

            <Box id={"alimonyChildren"} sx={summaryStyle.scrollToAnchor} />
            <AlimonyChildren formData={values} translate={translate} />

            <PersonData title={"Ihre finanzielle Situation"} formData={values} personType={"alimonyPetitioner"} translate={translate} />

            <Box id={"alimonyPartnerData"} sx={summaryStyle.scrollToAnchor} />
            <PersonData title={"Angaben zum anderen Elternteil"} formData={values} personType={"alimonyPartner"} translate={translate} />
          </>
        )}

        <Box id={"contactData"} sx={summaryStyle.scrollToAnchor} />
        <ContactData formData={values} product={product} withInsurance={withInsurance} />
      </Grid>

      <form onSubmit={handleSubmit}>
        <ValidatorTextField
          sx={summaryStyle.questionsTextArea}
          label="Ihr Anliegen"
          InputLabelProps={{
            shrink: true,
          }}
          placeholder="Sie haben Fragen/Anmerkungen? Dann beschreiben Sie diese hier gerne!"
          name="personalTextMessage"
          multiline
          rows={12}
          maxRows={12}
        />
        <Grid
          container
          sx={[
            {
              justifyContent: "center",
              alignItems: "baseline",
            },
            summaryStyle.applicationActions,
          ]}
        >
          <Grid
            sx={summaryStyle.backButtonContainer}
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Button fullWidth variant={"text"} component={Link} to={basePath + "/ihre-analyse"}>
              Angaben bearbeiten
            </Button>
          </Grid>
          <Grid
            sx={summaryStyle.submitButtonContainer}
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ButtonLoading
              fullWidth
              color={"primary"}
              variant={"contained"}
              onClick={handleCheckout}
              isLoading={isLoading}
              loadingText={"Dokument wird erstellt"}
            >
              Analyse erstellen
            </ButtonLoading>
          </Grid>
        </Grid>
      </form>
      <EmailVerificationModal />
    </ProductBox>
  );
}
